import type { Brand } from "../../../lib/ChatbotConfig";

/**
 * Marks the spot in a paragraph where the link to the brand's data protection
 * statement belongs. A paragraph without it is plain text.
 */
export const LINK_PLACEHOLDER = "{datenschutzerklaerung}";

/** The link's visible label, wherever it is rendered. */
export const LINK_LABEL = "Datenschutzerklärung";

export interface DataProtectionContent {
  headline: string;
  /** Rendered as one paragraph each, in order. See LINK_PLACEHOLDER. */
  paragraphs: string[];
  /**
   * Whether the link gets its own line below the paragraphs. Brands that carry
   * the link inline in their copy do not repeat it there.
   */
  standaloneLink: boolean;
  ctaLabel: string;
}

const defaultContent: DataProtectionContent = {
  headline: "Datenschutzhinweis",
  paragraphs: [
    "Lieber Bildungsfan, du wirst uns im Chat evtl. personenbezogene Daten mitteilen.",
    "Lies dazu unsere Informationen zum Datenschutz und akzeptiere diese, um deine Unterhaltung zu starten.",
  ],
  standaloneLink: true,
  ctaLabel: "Datenschutzerklärung akzeptieren",
};

const schoolsContent: DataProtectionContent = {
  headline: "Datenschutzhinweis",
  paragraphs: [
    `Da du hier eventuell persönliche Infos teilst, ist uns dein Datenschutz wichtig. Mit dem Start des Chats bestätigst du unsere ${LINK_PLACEHOLDER}.`,
  ],
  standaloneLink: false,
  ctaLabel: "Chat starten",
};

// One entry per brand rather than a branch in the component: every brand is
// expected to get its own wording eventually, and then a new text is an object
// literal here instead of another condition in the render tree.
const contentByBrand: Record<Brand, DataProtectionContent> = {
  training: defaultContent,
  group: defaultContent,
  schools: schoolsContent,
  academy: defaultContent,
};

export const getDataProtectionContent = (
  brand: Brand,
): DataProtectionContent =>
  // Plain-JS hosts embed the standalone bundle without TypeScript, so an unknown
  // brand can reach this — fall back the same way the link, the theme and the
  // deployment ID do.
  contentByBrand[brand] ?? defaultContent;
